<template>
  <div class="p-4">
    Mesh UVs ({{ mesh?.name }})
    <div
      v-for="uvName in uvs"
      :key="uvName"
      class="group hover:border-primary mx-2 flex items-center justify-between rounded border border-transparent px-2">
      <span class="text-sm text-gray-300">{{ uvName }}</span>
      <div class="flex items-center space-x-2">
        <span v-if="uvName" class="text-xs text-gray-400">
          {{ uvName }}
        </span>
        <div v-if="getUVAsset(mesh.name, uvName)">
          <span>{{ getUVAsset(mesh.name, uvName) }}</span>
          <DButton
            text
            size="small"
            class="p-1.5 text-gray-500 hover:text-red-400"
            @click="removeAsset(mesh.name, uvName)">
            <Trash class="h-4 w-4" />
          </DButton>
        </div>
        <DButton v-else size="small" @click="changeAsset(mesh.name, uvName)"> Select Asset </DButton>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useNodeManager } from '@/composables/useNodeManager';
import { useSelectionStore } from '@/stores/ui/selection';
import { getObjectByCode, getObjectByName } from '@/threejs/objectManager';
import { DButton, useDialog } from '@disguise/cloud-ui';
import { Trash } from 'lucide-vue-next';
import { computed, defineAsyncComponent, ref, watch } from 'vue';
const DriveSelector = defineAsyncComponent(() => import('@/components/dialogs/DriveSelector.vue'));

const dialog = useDialog();

const { findNodeById, updateNode } = useNodeManager();
const selectionStore = useSelectionStore();
const mesh = ref(null);

watch(
  () => selectionStore.selectedMeshId,
  (newMeshId) => {
    const object = getObjectByCode(selectionStore.selectedObjectId);
    mesh.value = null;
    object.traverse((child) => {
      if (child.uuid === newMeshId) {
        mesh.value = child;
      }
    });
  },
  { immediate: true }
);

const uvs = computed(() => {
  if (mesh.value) {
    return getUVNamesFromObject(mesh.value);
  }
  return [];
});

function getUVNamesFromObject(object) {
  const uvNames = [];

  // Check if object has geometry with UV attributes
  if (object.geometry && object.geometry.attributes) {
    const attributes = object.geometry.attributes;

    // Look for UV-related attributes
    Object.keys(attributes).forEach((key) => {
      if (key.toLowerCase().includes('uv')) {
        uvNames.push(key);
      }
    });
  }

  return uvNames;
}

const getUVAsset = (meshName, itemName) => {
  const nodeId = selectionStore.selectedObjectId;
  const node = findNodeById(nodeId);
  if (!node || !node.data.uvAssets) return null;
  // Search through uvAssets array for an object that has uvName as a key
  for (const assetObj of node.data.uvAssets) {
    if (assetObj[meshName] && assetObj[meshName].uv_channel === itemName) {
      return assetObj[meshName].resource_id;
    }
  }
  return null;
};

function removeAsset(objectName, uvChannelName = 'uv') {
  const object = getObjectByName(objectName);

  if (!object || !object.material) return;

  const nodeId = selectionStore.selectedObjectId;
  const node = findNodeById(nodeId);

  if (!node || !node.data.uvAssets) return;

  const uvAssets = node.data.uvAssets || [];

  // Remove the entry for the specified objectName
  const updatedUvAssets = uvAssets.filter((assetObj) => !assetObj.hasOwnProperty(objectName));
  updateNode(nodeId, { uvAssets: updatedUvAssets });
}

function changeAsset(objectName, uvChannelName = 'uv') {
  dialog.open(DriveSelector, {
    data: {
      filter: 'media',
    },
    props: {
      header: 'Asset List',
      style: {
        width: '50vw',
      },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw',
      },
      modal: true,
    },
    onClose: async (options) => {
      if (options.data?.code) {
        const targetNodeId = selectionStore.selectedObjectId;

        const node = findNodeById(targetNodeId);
        if (!node) return;

        const uvAssets = node.data.uvAssets ? [...node.data.uvAssets] : [];

        // Check if objectName already exists in uvAssets array
        let found = false;
        for (const assetObj of uvAssets) {
          if (assetObj[objectName]) {
            // Update existing resource_id
            assetObj[objectName].resource_id = options.data.code;
            assetObj[objectName].uv_channel = uvChannelName;
            found = true;
            break;
          }
        }

        // If not found, add new entry
        if (!found) {
          uvAssets.push({
            [objectName]: {
              resource_id: options.data.code,
              uv_channel: uvChannelName,
            },
          });
        }
        updateNode(targetNodeId, { uvAssets });
      }
    },
  });
}
</script>
